import { useState, useEffect, useCallback } from 'react';
import {
  AlignLeft, AlignCenter, AlignRight,
  Bold, Minus, Plus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  CANVAS_FONTS,
  TEXT_COLORS,
  TEXT_EFFECTS,
  TEXT_PRESETS,
  loadFont,
  preloadAllFonts,
  applyTextEffect,
} from './utils/fonts';

/**
 * Text style panel — font picker, size, color, alignment, effects, presets.
 * Operates on the currently selected Fabric.js text object.
 */
export const TextPanel = ({ fabricRef, selectedObject, onModified, isDark }) => {
  const [activeTab, setActiveTab] = useState('presets');
  const [textProps, setTextProps] = useState({
    fontFamily: 'Inter',
    fontSize: 48,
    fill: '#FFFFFF',
    fontWeight: '400',
    textAlign: 'center',
    charSpacing: 0,
    effect: 'none',
  });

  // Preload fonts on mount
  useEffect(() => {
    preloadAllFonts();
  }, []);

  // Sync panel state with selected object
  useEffect(() => {
    if (!selectedObject || selectedObject.type !== 'textbox') return;
    setTextProps({
      fontFamily: selectedObject.fontFamily || 'Inter',
      fontSize: selectedObject.fontSize || 48,
      fill: selectedObject.fill || '#FFFFFF',
      fontWeight: String(selectedObject.fontWeight || '400'),
      textAlign: selectedObject.textAlign || 'center',
      charSpacing: selectedObject.charSpacing || 0,
      effect: selectedObject.__textEffect || 'none',
    });
  }, [selectedObject]);

  const updateText = useCallback((props, effectKey) => {
    if (!selectedObject || selectedObject.type !== 'textbox') return;

    const newProps = { ...textProps, ...props };
    if (effectKey !== undefined) newProps.effect = effectKey;

    selectedObject.set({
      fontFamily: newProps.fontFamily,
      fontSize: newProps.fontSize,
      fill: newProps.fill,
      fontWeight: newProps.fontWeight,
      textAlign: newProps.textAlign,
      charSpacing: newProps.charSpacing,
    });

    if (effectKey !== undefined) {
      selectedObject.__textEffect = effectKey;
      applyTextEffect(selectedObject, effectKey);
    }

    setTextProps(newProps);
    fabricRef.current?.renderAll();
    onModified?.();
  }, [selectedObject, textProps, fabricRef, onModified]);

  const applyPreset = useCallback(async (preset) => {
    if (!selectedObject || selectedObject.type !== 'textbox') return;

    await loadFont(preset.fontFamily);

    selectedObject.set({
      fontFamily: preset.fontFamily,
      fontSize: preset.fontSize,
      fill: preset.fill,
      fontWeight: preset.fontWeight,
      textAlign: preset.textAlign,
      charSpacing: preset.charSpacing,
    });

    selectedObject.__textEffect = preset.effect;
    applyTextEffect(selectedObject, preset.effect);

    setTextProps({
      fontFamily: preset.fontFamily,
      fontSize: preset.fontSize,
      fill: preset.fill,
      fontWeight: preset.fontWeight,
      textAlign: preset.textAlign,
      charSpacing: preset.charSpacing,
      effect: preset.effect,
    });

    fabricRef.current?.renderAll();
    onModified?.();
  }, [selectedObject, fabricRef, onModified]);

  const handleFontSelect = useCallback(async (font) => {
    await loadFont(font.family);
    updateText({ fontFamily: font.family });
  }, [updateText]);

  const mutedClass = isDark ? 'text-white/40' : 'text-gray-400';
  const textClass = isDark ? 'text-white' : 'text-gray-900';
  const borderClass = isDark ? 'border-white/10' : 'border-gray-200';
  const activeBg = isDark ? 'bg-white/10' : 'bg-gray-100';
  const hoverBg = isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50';

  const isTextSelected = selectedObject?.type === 'textbox';

  if (!isTextSelected) {
    return (
      <div className="p-4 text-center">
        <p className={cn("text-sm", mutedClass)}>Select a text layer to edit</p>
        <p className={cn("text-xs mt-1", mutedClass)}>Tap + Text to add one</p>
      </div>
    );
  }

  const tabs = [
    { key: 'presets', label: 'Presets' },
    { key: 'font',   label: 'Font' },
    { key: 'style',  label: 'Style' },
  ];

  return (
    <div className="p-3 space-y-2.5">
      {/* Tab bar */}
      <div className={cn("flex gap-1 border-b pb-1.5", borderClass)}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              "px-3 py-1.5 text-[11px] font-display tracking-widest uppercase rounded-sm transition-colors",
              activeTab === tab.key ? cn(textClass, activeBg) : mutedClass,
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Presets tab */}
      {activeTab === 'presets' && (
        <div className="grid grid-cols-4 gap-1.5">
          {TEXT_PRESETS.map((preset) => (
            <button
              key={preset.key}
              onClick={() => applyPreset(preset)}
              className={cn(
                "flex flex-col items-center gap-0.5 p-2.5 border rounded-sm transition-colors",
                borderClass, hoverBg
              )}
            >
              <span
                className="text-xl leading-none truncate w-full text-center"
                style={{
                  fontFamily: `"${preset.fontFamily}", sans-serif`,
                  color: preset.fill,
                  fontWeight: preset.fontWeight,
                  textShadow: preset.effect === 'shadow' ? '2px 2px 4px rgba(0,0,0,0.5)' :
                              preset.effect === 'glow' ? `0 0 10px ${preset.fill}` :
                              preset.effect === 'outline' ? '1px 1px 0 #000, -1px -1px 0 #000' : 'none',
                }}
              >
                Aa
              </span>
              <span className={cn("text-[10px] font-display tracking-wider uppercase", mutedClass)}>
                {preset.label}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Font picker tab */}
      {activeTab === 'font' && (
        <div className="max-h-36 overflow-y-auto space-y-0.5">
          {CANVAS_FONTS.map((font) => (
            <button
              key={font.key}
              onClick={() => handleFontSelect(font)}
              className={cn(
                "w-full text-left px-3 py-2.5 text-sm rounded-sm transition-colors",
                textProps.fontFamily === font.family ? cn(textClass, activeBg) : cn(mutedClass, hoverBg),
              )}
              style={{ fontFamily: `"${font.family}", sans-serif` }}
            >
              {font.family}
            </button>
          ))}
        </div>
      )}

      {/* Style controls tab */}
      {activeTab === 'style' && (
        <div className="space-y-2.5">
          {/* Font size */}
          <div className="flex items-center gap-2">
            <span className={cn("text-[10px] font-display tracking-widest uppercase w-8 shrink-0", mutedClass)}>
              Size
            </span>
            <button
              onClick={() => updateText({ fontSize: Math.max(12, textProps.fontSize - 4) })}
              className={cn("p-2 border rounded-sm", borderClass, mutedClass, hoverBg)}
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
            <span className={cn("text-sm font-mono min-w-[3ch] text-center", textClass)}>
              {textProps.fontSize}
            </span>
            <button
              onClick={() => updateText({ fontSize: Math.min(200, textProps.fontSize + 4) })}
              className={cn("p-2 border rounded-sm", borderClass, mutedClass, hoverBg)}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
            <input
              type="range"
              min={12}
              max={200}
              value={textProps.fontSize}
              onChange={(e) => updateText({ fontSize: parseInt(e.target.value) })}
              className="flex-1 h-1 accent-amber-500"
            />
          </div>

          {/* Color swatches */}
          <div className="flex flex-wrap gap-2">
            {TEXT_COLORS.map((color) => (
              <button
                key={color}
                onClick={() => {
                  updateText({ fill: color });
                  if (textProps.effect === 'glow') {
                    updateText({ fill: color }, 'glow');
                  }
                }}
                className={cn(
                  "w-8 h-8 rounded-sm border transition-transform hover:scale-110",
                  textProps.fill === color
                    ? 'border-amber-500 ring-1 ring-amber-500'
                    : isDark ? 'border-white/20' : 'border-gray-200'
                )}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>

          {/* Alignment + Weight — inline row */}
          <div className="flex items-center gap-3">
            <div className={cn("flex border rounded-sm", borderClass)}>
              {[
                { key: 'left',   icon: AlignLeft },
                { key: 'center', icon: AlignCenter },
                { key: 'right',  icon: AlignRight },
              ].map(({ key, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => updateText({ textAlign: key })}
                  className={cn(
                    "p-2 transition-colors",
                    textProps.textAlign === key ? cn(textClass, activeBg) : mutedClass,
                    hoverBg,
                  )}
                >
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>
            <button
              onClick={() => updateText({ fontWeight: textProps.fontWeight === '700' ? '400' : '700' })}
              className={cn(
                "p-2 border rounded-sm transition-colors",
                borderClass,
                textProps.fontWeight === '700' ? cn(textClass, activeBg) : mutedClass,
                hoverBg,
              )}
            >
              <Bold className="h-4 w-4" />
            </button>
            <div className="flex-1">
              <input
                type="range"
                min={-100}
                max={400}
                value={textProps.charSpacing}
                onChange={(e) => updateText({ charSpacing: parseInt(e.target.value) })}
                className="w-full h-1 accent-amber-500"
              />
            </div>
          </div>

          {/* Effects */}
          <div className="flex flex-wrap gap-1.5">
            {TEXT_EFFECTS.map((effect) => (
              <button
                key={effect.key}
                onClick={() => updateText({}, effect.key)}
                className={cn(
                  "px-3 py-1.5 text-[11px] font-display tracking-wider uppercase border rounded-sm transition-colors",
                  textProps.effect === effect.key
                    ? 'border-amber-500 text-amber-500'
                    : cn(borderClass, mutedClass),
                  hoverBg,
                )}
              >
                {effect.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
